import { HStack, Stack } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import ProceedCancelButtons from "components/Button/ProceedCancelButtons";
import { AdminCompany } from "models/company";
import { FloatingField } from "design-system";
import { useCurrentCompanyAdminQuery, useUpdateCompany } from "features/Company/hooks";
import {
  BUDDY_EXPECTATIONS_FIELD,
  BUDDY_SELECTION_FIELD,
  ONBOARDING_PLAN_TEMPLATES_FIELD,
  BENEFITS_INFO_FIELD,
  HRIS_FIELD,
  COMPANY_HANDBOOK_FIELD,
  COMPANY_WIKI_FIELD,
  COMPANY_GOALS_FIELD,
  Schema,
  schema,
} from "./definitions";

const values = (company: AdminCompany | null | undefined) => {
  if (!company) return {};
  const {
    newHireInfoPacketUrl,
    benefitsInfoUrl,
    hrisUrl,
    companyHandbookUrl,
    companyWikiUrl,
    companyGoalsUrl,
    buddyExpectationsUrl,
    buddySelectionUrl,
    onboardingPlanTemplatesUrl,
  } = company;
  return {
    newHireInfoPacketUrl,
    benefitsInfoUrl,
    hrisUrl,
    companyHandbookUrl,
    companyWikiUrl,
    companyGoalsUrl,
    buddyExpectationsUrl,
    buddySelectionUrl,
    onboardingPlanTemplatesUrl,
  };
};

export default function CompanyResourcesForm({ onClose = () => {} }: { onClose: () => void }) {
  const { data: company } = useCurrentCompanyAdminQuery();
  const defaultValues: Partial<Schema> = values(company);
  const { mutate, isPending } = useUpdateCompany();
  const {
    control,
    formState: { errors, isDirty, dirtyFields },
    handleSubmit,
    reset,
  } = useForm<Schema>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    values: defaultValues,
  });

  return (
    <Stack alignItems="start">
      <FloatingField name={BUDDY_EXPECTATIONS_FIELD.name} errors={errors.buddyExpectationsUrl?.message}>
        <FloatingField.Label>{BUDDY_EXPECTATIONS_FIELD.label}</FloatingField.Label>
        <FloatingField.Text control={control} name={BUDDY_EXPECTATIONS_FIELD.name} type="url" />
      </FloatingField>
      <FloatingField name={BUDDY_SELECTION_FIELD.name} errors={errors.buddySelectionUrl?.message}>
        <FloatingField.Label>{BUDDY_SELECTION_FIELD.label}</FloatingField.Label>
        <FloatingField.Text control={control} name={BUDDY_SELECTION_FIELD.name} type="url" />
      </FloatingField>
      <FloatingField name={ONBOARDING_PLAN_TEMPLATES_FIELD.name} errors={errors.onboardingPlanTemplatesUrl?.message}>
        <FloatingField.Label>{ONBOARDING_PLAN_TEMPLATES_FIELD.label}</FloatingField.Label>
        <FloatingField.Text control={control} name={ONBOARDING_PLAN_TEMPLATES_FIELD.name} type="url" />
      </FloatingField>
      <FloatingField name={COMPANY_WIKI_FIELD.name} errors={errors.companyWikiUrl?.message}>
        <FloatingField.Label>{COMPANY_WIKI_FIELD.label}</FloatingField.Label>
        <FloatingField.Text control={control} name={COMPANY_WIKI_FIELD.name} type="url" />
      </FloatingField>
      <FloatingField name={COMPANY_HANDBOOK_FIELD.name} errors={errors.companyHandbookUrl?.message}>
        <FloatingField.Label>{COMPANY_HANDBOOK_FIELD.label}</FloatingField.Label>
        <FloatingField.Text control={control} name={COMPANY_HANDBOOK_FIELD.name} type="url" />
      </FloatingField>
      <FloatingField name={COMPANY_GOALS_FIELD.name} errors={errors.companyGoalsUrl?.message}>
        <FloatingField.Label>{COMPANY_GOALS_FIELD.label}</FloatingField.Label>
        <FloatingField.Text control={control} name={COMPANY_GOALS_FIELD.name} type="url" />
      </FloatingField>
      <FloatingField name={HRIS_FIELD.name} errors={errors.hrisUrl?.message}>
        <FloatingField.Label>{HRIS_FIELD.label}</FloatingField.Label>
        <FloatingField.Text control={control} name={HRIS_FIELD.name} type="url" />
      </FloatingField>
      <FloatingField name={BENEFITS_INFO_FIELD.name} errors={errors.benefitsInfoUrl?.message}>
        <FloatingField.Label>{BENEFITS_INFO_FIELD.label}</FloatingField.Label>
        <FloatingField.Text control={control} name={BENEFITS_INFO_FIELD.name} type="url" />
      </FloatingField>

      <HStack my={8} alignSelf="end" justify="end">
        <ProceedCancelButtons
          handleSubmit={handleSubmit((data) =>
            mutate(
              { data, dirtyFields },
              {
                onSuccess: () => {
                  reset();
                  onClose();
                },
              },
            ),
          )}
          onClose={onClose}
          proceedDisabled={!isDirty}
          proceedText="Update Resources"
          isLoading={isPending}
        />
      </HStack>
    </Stack>
  );
}
